import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  AlertTriangle, Trash2, RefreshCw, Shield, Database,
  Users, FileText, DollarSign, Calendar, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/api/api';
const DataResetPage = () => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [protectedData, setProtectedData] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [keepMasterSettings, setKeepMasterSettings] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const [previewRes, protectedRes] = await Promise.all([
        api.get('/data-reset/preview'),
        api.get('/data-reset/protected-data')
      ]);
      setPreview(previewRes.data);
      setProtectedData(protectedRes.data.protected_collections || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data preview');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE ALL DATA') {
      toast.error('Please type "DELETE ALL DATA" exactly to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.post('/data-reset/execute', {
        confirmation_text: confirmText,
        keep_users: true,
        keep_master_settings: keepMasterSettings
      });
      
      setDeleteResult(response.data);
      setShowConfirmDialog(false);
      toast.success(`Data reset complete! ${response.data.deleted_counts ? Object.values(response.data.deleted_counts).reduce((a, b) => a + b, 0) : 0} records deleted.`);
      fetchPreview();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset data');
    } finally {
      setIsDeleting(false);
      setConfirmText('');
    }
  };

  const getCategoryIcon = (name) => {
    if (name.includes('student') || name.includes('admission')) return <Users className="h-4 w-4" />;
    if (name.includes('fee') || name.includes('payment') || name.includes('caution')) return <DollarSign className="h-4 w-4" />;
    if (name.includes('attendance') || name.includes('leave')) return <Calendar className="h-4 w-4" />;
    if (name.includes('exam') || name.includes('mark') || name.includes('transcript')) return <BookOpen className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-reset-page">
      {/* Warning Header */}
      <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <AlertTriangle className="h-12 w-12 text-red-600 flex-shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-red-700">Data Reset - Danger Zone</h1>
            <p className="text-red-600 mt-2">
              This action will permanently delete all data from your institution except login credentials.
              <strong> This cannot be undone!</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data to be Deleted */}
        <Card className="border-red-200">
          <CardHeader className="bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Data to be Deleted
            </CardTitle>
            <CardDescription>
              Total: <strong className="text-red-600">{preview?.total_records_to_delete || 0}</strong> records
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {preview?.collections_to_delete?.length > 0 ? (
              <div className="space-y-2">
                {preview.collections_to_delete.map((col) => (
                  <div key={col.name} className="flex items-center justify-between p-2 bg-red-50 rounded border border-red-100">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(col.name)}
                      <span className="text-sm font-medium">{col.name.replace(/_/g, ' ')}</span>
                    </div>
                    <Badge variant="destructive">{col.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No data to delete</p>
            )}
          </CardContent>
        </Card>

        {/* Protected Data */}
        <Card className="border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Shield className="h-5 w-5" />
              Protected Data (Will NOT be deleted)
            </CardTitle>
            <CardDescription>Login credentials and settings are always preserved</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {protectedData.map((col) => (
                <div key={col.name} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-100">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">{col.name.replace(/_/g, ' ')}</span>
                  </div>
                  <Badge className="bg-green-100 text-green-700">{col.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Master Settings Option */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Master Settings
          </CardTitle>
          <CardDescription>Choose whether to keep or delete master settings data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3">
            <Checkbox 
              id="keepMaster" 
              checked={keepMasterSettings}
              onCheckedChange={(checked) => setKeepMasterSettings(checked)}
            />
            <label htmlFor="keepMaster" className="text-sm font-medium cursor-pointer">
              Keep Master Settings (Courses, Subjects, Fee Structures, etc.)
            </label>
          </div>
          
          {preview?.master_settings?.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700 mb-2">
                {keepMasterSettings ? '✓ These will be preserved:' : '⚠ These will also be deleted:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {preview.master_settings.map((col) => (
                  <Badge 
                    key={col.name} 
                    variant="outline" 
                    className={keepMasterSettings ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}
                  >
                    {col.name.replace(/_/g, ' ')} ({col.count})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Result */}
      {deleteResult && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-700">Reset Complete!</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-green-600 mb-3">
              Successfully deleted {Object.values(deleteResult.deleted_counts || {}).reduce((a, b) => a + b, 0)} records.
            </p>
            {deleteResult.deleted_counts && Object.keys(deleteResult.deleted_counts).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(deleteResult.deleted_counts).map(([name, count]) => (
                  <Badge key={name} variant="outline" className="bg-white">
                    {name}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button onClick={fetchPreview} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh Preview
        </Button>
        
        <Button 
          onClick={() => setShowConfirmDialog(true)}
          variant="destructive"
          className="bg-red-600 hover:bg-red-700"
          disabled={!preview?.total_records_to_delete}
        >
          <Trash2 className="h-4 w-4 mr-2" /> 
          Delete All Data ({preview?.total_records_to_delete || 0} records)
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              Confirm Data Reset
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-red-600 font-medium">
                  You are about to permanently delete {preview?.total_records_to_delete || 0} records. 
                  This action CANNOT be undone!
                </p>
                
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-700 mb-2">What will be deleted:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    <li>All student records and admissions</li>
                    <li>All fee payments and receipts</li>
                    <li>All attendance records</li>
                    <li>All examination results</li>
                    {!keepMasterSettings && <li>All master settings (courses, subjects, etc.)</li>}
                  </ul>
                </div>

                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-700 mb-2">What will be preserved:</p>
                  <ul className="text-sm text-green-600 list-disc list-inside">
                    <li>All user login credentials</li>
                    <li>Institution settings</li>
                    {keepMasterSettings && <li>Master settings (courses, subjects, etc.)</li>}
                  </ul>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Type <span className="font-bold text-red-600">DELETE ALL DATA</span> to confirm:
                  </label>
                  <Input 
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type here to confirm"
                    className="border-red-300 focus:border-red-500"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE ALL DATA' || isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Yes, Delete Everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DataResetPage;
